import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudQueueOutlinedIcon from "@mui/icons-material/CloudQueueOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import GlassCard from "../../components/dashboard/GlassCard";
import Header from "../../components/Header";
import saasApi from "../../utils/saasApi";
import { useTranslation } from "react-i18next";

const statusColor = (status) => {
  if (["online", "configured"].includes(status)) return "success";
  if (["degraded", "stopped", "not_configured", "not_created", "starting", "creating"].includes(status)) return "warning";
  if (["offline", "error"].includes(status)) return "error";
  return "default";
};

const statusLabel = (status, t) => ({
  online: t("platform.status.operational"),
  configured: t("platform.status.configured"),
  degraded: t("platform.status.degraded"),
  stopped: t("platform.status.stopped"),
  not_configured: t("platform.common.notConfigured"),
  not_created: t("platform.status.notCreated"),
  offline: t("platform.status.offline"),
  error: "Error",
  starting: t("platform.status.starting"),
  creating: t("platform.status.creating"),
}[status] || status || t("platform.status.unknown"));

const StatusIcon = ({ status }) => {
  if (["online", "configured"].includes(status)) return <CheckCircleOutlineIcon color="success" />;
  if (["offline", "error"].includes(status)) return <ErrorOutlineIcon color="error" />;
  return <WarningAmberOutlinedIcon color="warning" />;
};

const ComponentIcon = ({ name }) => {
  if (name === "database" || name === "storage") return <StorageOutlinedIcon color="primary" />;
  if (name === "cameras") return <VideocamOutlinedIcon color="primary" />;
  return <CloudQueueOutlinedIcon color="primary" />;
};

const formatDate = (value, t, locale) => {
  if (!value) return t("platform.system.noHeartbeat");
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(locale || "es-AR");
};

const SystemHealth = () => {
  const { t, i18n } = useTranslation();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setHealth(await saasApi.getHealth());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 30000);
    return () => clearInterval(id);
  }, [fetchHealth]);

  const checks = useMemo(() => health?.checks || [], [health]);
  const degraded = useMemo(() => checks.filter((check) => check.severity !== "ok"), [checks]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box className="scene-toolbar" display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
        <Header title={t("platform.pages.systemHealth.title")} subtitle={t("platform.pages.systemHealth.subtitle")} />
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchHealth} disabled={loading}>{t("common.refresh")}</Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {health && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <GlassCard hover={false} sx={{ minHeight: 170 }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={2}>
                  <Box>
                    <Typography variant="h4">{t("platform.system.operationalHealth")}</Typography>
                    <Typography color="text.secondary">{t("platform.system.checkedAt")}: {formatDate(health.checked_at, t, i18n.language)}</Typography>
                    <Typography color="text.secondary">{t("platform.system.lastHeartbeat")}: {formatDate(health.last_heartbeat, t, i18n.language)}</Typography>
                  </Box>
                  <Chip label={statusLabel(health.status, t)} color={statusColor(health.status)} sx={{ fontWeight: 600 }} />
                </Stack>
              </GlassCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <GlassCard hover={false}><Typography color="text.secondary">{t("platform.system.checks")}</Typography><Typography variant="h4">{health.summary?.total || 0}</Typography></GlassCard>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <GlassCard hover={false}><Typography color="text.secondary">OK</Typography><Typography variant="h4" color="success.main">{health.summary?.ok || 0}</Typography></GlassCard>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <GlassCard hover={false}><Typography color="text.secondary">{t("platform.system.warnings")}</Typography><Typography variant="h4" color="warning.main">{health.summary?.warning || 0}</Typography></GlassCard>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <GlassCard hover={false}><Typography color="text.secondary">{t("platform.system.critical")}</Typography><Typography variant="h4" color="error.main">{health.summary?.critical || 0}</Typography></GlassCard>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {degraded.length > 0 && (
            <Alert severity={degraded.some((check) => check.severity === "critical") ? "error" : "warning"} sx={{ mb: 2 }}>
              {t("platform.system.degradedAlert", { count: degraded.length, components: degraded.map((check) => check.label || check.name).join(", ") })}
            </Alert>
          )}

          <Grid container spacing={2.5}>
            {checks.map((check) => (
              <Grid item xs={12} md={6} lg={4} key={check.name}>
                <GlassCard hover={false} sx={{ minHeight: 220 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                    <Stack direction="row" gap={1.5} alignItems="center">
                      <ComponentIcon name={check.name} />
                      <Box>
                        <Typography variant="h5">{check.label || check.name.replace(/_/g, " ")}</Typography>
                        <Typography variant="body2" color="text.secondary">{check.message || t("platform.system.noOperationalDetail")}</Typography>
                      </Box>
                    </Stack>
                    <StatusIcon status={check.status} />
                  </Stack>

                  <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
                    <Chip label={statusLabel(check.status, t)} color={statusColor(check.status)} size="small" />
                    <Chip label={`${t("platform.system.heartbeat")}: ${formatDate(check.last_heartbeat, t, i18n.language)}`} size="small" variant="outlined" />
                  </Stack>

                  <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                    {Object.entries(check.details || {})
                      .filter(([key]) => !["message", "last_heartbeat", "last_health_at"].includes(key))
                      .slice(0, 6)
                      .map(([key, value]) => (
                        <Box key={key} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                          <Typography variant="caption" color="text.secondary">{key.replace(/_/g, " ")}</Typography>
                          <Typography variant="body2" fontWeight={700} noWrap>{value == null ? "-" : String(value)}</Typography>
                        </Box>
                      ))}
                  </Box>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default SystemHealth;
